import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import moment from 'moment';

import { Skeleton, SkeletonTable, SkeletonCards } from '../SpotlightsSkeleton';
import { getAutomation } from '../../../api/spotlights';
import { fmtInt } from '../../../helpers/spotlights_dashboard_helpers';
import RoutineEditorModal from '../RoutineEditorModal';

const STATUSES = [
  { key: 'pending', label: 'Pending' },
  { key: 'claimed', label: 'Claimed' },
  { key: 'failed', label: 'Failed' },
  { key: 'done', label: 'Done' },
  { key: 'all', label: 'All' }
];

const STATUS_PILL = { pending: 'amber', claimed: 'blue', failed: 'red', done: 'green', skipped: '' };

// Health/readiness of a connected client → pill color + label.
const HEALTH_PILL = { online: 'green', stale: 'amber', needs_login: 'red' };
const HEALTH_LABEL = { online: 'Online', stale: 'Stale', needs_login: 'Needs login' };

// One-line run-state summary for a connected client.
const clientStateLabel = c => {
  if (c.verified === false) return 'Signed out of ShopMy admin';
  if (c.needsLogin) return 'Waiting on sign-in / checkpoint';
  if (c.paused) return 'Paused';
  if (!c.running) return 'Stopped (idle)';
  if (c.phase && c.phase !== 'idle') return `Working (${c.phase})`;
  return 'Running (idle)';
};

// How often the dashboard silently re-pulls so the live health view stays current.
const REFRESH_MS = 20000;

// First-load skeleton that mirrors the tab's three titled tables (task queue, routines, clients)
// with their real column counts, so the placeholder resembles the content that lands.
const SECTION_SKELETONS = [
  { titleW: '22%', rows: 6, cols: 11 }, // Task queue
  { titleW: '34%', rows: 3, cols: 7 }, // Active automation routines
  { titleW: '26%', rows: 4, cols: 9 } // Connected clients
];

const AutomationSkeleton = () => (
  <div>
    {SECTION_SKELETONS.map((s, i) => (
      <div className='sl-section' key={i}>
        <div className='sl-section-title'>
          <Skeleton w={s.titleW} h={15} />
        </div>
        <SkeletonTable rows={s.rows} cols={s.cols} />
      </div>
    ))}
  </div>
);

const SpotlightsAutomation = () => {
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // null = closed; { routine } where routine is null (new) or an existing routine.
  const [editor, setEditor] = useState(null);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const r = await getAutomation({ status });
        setData(r);
      } catch (e) {
        setError(typeof e === 'string' ? e : 'Failed to load ad automation.');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [status]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Keep the connected-clients health view fresh without a full reload flicker.
  useEffect(() => {
    const t = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className='spotlights-automation'>
      {/* Status counts */}
      {data?.counts ? (
        <div className='sl-card-grid'>
          {Object.keys(STATUS_PILL).map(s => (
            <div key={s} className='sl-card'>
              <div className='sl-card-label'>{s}</div>
              <div className='sl-card-value'>{fmtInt(data.counts[s] || 0)}</div>
            </div>
          ))}
        </div>
      ) : loading && !data ? (
        <SkeletonCards count={Object.keys(STATUS_PILL).length} />
      ) : null}

      <div className='sl-controls'>
        <div className='sl-segmented'>
          {STATUSES.map(s => (
            <div key={s.key} className={cn('sl-segment', { active: status === s.key })} onClick={() => setStatus(s.key)}>
              {s.label}
            </div>
          ))}
        </div>
        <button className='sl-btn secondary small' onClick={load} style={{ marginLeft: 'auto' }}>
          Refresh
        </button>
      </div>

      {error ? (
        <div className='sl-error'>{error}</div>
      ) : loading && !data ? (
        <AutomationSkeleton />
      ) : (
        <div className={cn('sl-refreshable', { 'sl-refreshing': loading })}>
          {/* Task queue */}
          <div className='sl-section'>
            <div className='sl-section-title'>Task queue</div>
            <table className='sl-table'>
              <thead>
                <tr>
                  <th className='no-sort'>Task</th>
                  <th className='no-sort'>Brand</th>
                  <th className='no-sort'>Creator</th>
                  <th className='no-sort'>Platform</th>
                  <th className='no-sort'>Phase</th>
                  <th className='no-sort'>Action</th>
                  <th className='no-sort'>Status</th>
                  <th className='no-sort'>Handled by</th>
                  <th className='no-sort num'>Attempts</th>
                  <th className='no-sort'>Created</th>
                  <th className='no-sort'>Links</th>
                </tr>
              </thead>
              <tbody>
                {(data?.tasks || []).map(t => (
                  <tr key={t.taskId} style={t.status === 'failed' ? { background: 'rgba(211,0,0,0.05)' } : undefined}>
                    <td>
                      #{t.taskId} {t.isTest && <span className='sl-pill'>test</span>}
                    </td>
                    <td>{t.brandName}</td>
                    <td>{t.creatorName}</td>
                    <td>{t.platform}</td>
                    <td>{t.phase || '—'}</td>
                    <td>{t.action}</td>
                    <td>
                      <span className={cn('sl-pill', STATUS_PILL[t.status])}>{t.status}</span>
                    </td>
                    <td>
                      {t.handledBy && (t.handledBy.username || t.handledBy.name || t.handledBy.label) ? (
                        <span title={[t.handledBy.label, t.handledBy.at && moment(t.handledBy.at).format('MMM D, h:mma')].filter(Boolean).join(' · ')}>
                          {t.handledBy.username ? `@${t.handledBy.username}` : t.handledBy.name || t.handledBy.label}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className='num'>{t.attempts}</td>
                    <td>{moment(t.createdAt).fromNow()}</td>
                    <td>
                      {t.automationUrl && (
                        <a href={t.automationUrl} target='_blank' rel='noreferrer'>
                          Ad ↗
                        </a>
                      )}
                      {t.lastError && (
                        <span className='sl-pill red' title={t.lastError} style={{ marginLeft: 6 }}>
                          error
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {(!data?.tasks || !data.tasks.length) && (
                  <tr>
                    <td colSpan={11} className='sl-empty'>
                      No tasks.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Routines */}
          <div className='sl-section'>
            <div className='sl-section-title' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Active automation routines</span>
              <button className='sl-btn small' onClick={() => setEditor({ routine: null })}>
                New routine
              </button>
            </div>
            <table className='sl-table'>
              <thead>
                <tr>
                  <th className='no-sort'>Platform</th>
                  <th className='no-sort'>Routine</th>
                  <th className='no-sort'>Label</th>
                  <th className='no-sort num'>Version</th>
                  <th className='no-sort num'>Steps</th>
                  <th className='no-sort'>Notes</th>
                  <th className='no-sort' />
                </tr>
              </thead>
              <tbody>
                {(data?.routines || []).map(r => (
                  <tr key={`${r.platform}-${r.routineKey}`}>
                    <td>{r.platform}</td>
                    <td>{r.routineKey}</td>
                    <td>{r.label || '—'}</td>
                    <td className='num'>v{r.version}</td>
                    <td className='num'>{Array.isArray(r.steps) ? r.steps.length : '—'}</td>
                    <td style={{ maxWidth: 320, whiteSpace: 'normal' }}>{r.notes || '—'}</td>
                    <td>
                      <button className='sl-btn small secondary' onClick={() => setEditor({ routine: r })}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {(!data?.routines || !data.routines.length) && (
                  <tr>
                    <td colSpan={7} className='sl-empty'>
                      No active routines.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Connected clients */}
          <div className='sl-section'>
            <div className='sl-section-title'>Connected clients{data?.clients ? ` (${data.clients.length})` : ''}</div>
            <table className='sl-table'>
              <thead>
                <tr>
                  <th className='no-sort'>Operator / Device</th>
                  <th className='no-sort'>Health</th>
                  <th className='no-sort'>Ready</th>
                  <th className='no-sort'>ShopMy admin</th>
                  <th className='no-sort'>Ad Manager</th>
                  <th className='no-sort'>State</th>
                  <th className='no-sort num'>Queue</th>
                  <th className='no-sort'>Version</th>
                  <th className='no-sort'>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {(data?.clients || []).map(c => (
                  <tr key={c.clientId}>
                    <td>
                      {c.verified === false ? (
                        <span className='sl-pill amber' title='Reported without a ShopMy admin session — identity unverified.'>
                          Not signed in
                        </span>
                      ) : c.operator ? (
                        c.operator.username ? `@${c.operator.username}` : c.operator.name
                      ) : (
                        'Unknown operator'
                      )}
                      {c.deviceNickname && <div style={{ color: '#888', fontSize: 11 }}>{c.deviceNickname}</div>}
                    </td>
                    <td>
                      <span className={cn('sl-pill', HEALTH_PILL[c.health] || '')}>{HEALTH_LABEL[c.health] || c.health}</span>
                    </td>
                    <td>
                      <span className={cn('sl-pill', c.ready ? 'green' : 'amber')}>{c.ready ? 'Ready' : 'Not ready'}</span>
                    </td>
                    <td>
                      <span className={cn('sl-pill', c.shopmyAdmin ? 'green' : 'red')}>{c.shopmyAdmin ? 'Yes' : 'No'}</span>
                    </td>
                    <td>
                      <span className={cn('sl-pill', c.adManagerLogins?.meta ? 'green' : '')} style={{ marginRight: 4 }}>
                        Meta {c.adManagerLogins?.meta ? '✓' : '✗'}
                      </span>
                      <span className={cn('sl-pill', c.adManagerLogins?.tiktok ? 'green' : '')}>TikTok {c.adManagerLogins?.tiktok ? '✓' : '✗'}</span>
                    </td>
                    <td>
                      {clientStateLabel(c)}
                      {c.lastError && (
                        <span className='sl-pill red' title={c.lastError} style={{ marginLeft: 6 }}>
                          error
                        </span>
                      )}
                    </td>
                    <td className='num'>{fmtInt(c.queueCount || 0)}</td>
                    <td>{c.version || '—'}</td>
                    <td title={c.lastSeenAt ? moment(c.lastSeenAt).format('MMM D, h:mm:ssa') : ''}>
                      {c.lastSeenAt ? moment(c.lastSeenAt).fromNow() : '—'}
                    </td>
                  </tr>
                ))}
                {(!data?.clients || !data.clients.length) && (
                  <tr>
                    <td colSpan={9} className='sl-empty'>
                      No connected clients. An operator's browser appears here while the Spotlights Ad Automation extension is installed and open — including, marked “Not signed in”, when it's running but signed out of ShopMy admin.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editor && (
        <RoutineEditorModal
          routine={editor.routine}
          onClose={() => setEditor(null)}
          onSaved={() => load(true)}
        />
      )}
    </div>
  );
};

export default SpotlightsAutomation;
